import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query'
import { getReturnOrderSearch, getReturnWorkScanItems, getReturnWorkScanSummary, saveReturnWorkScan } from './apis'
import { toReturnWorkScanHead } from './mappers'
import {
  ReturnOrderSearchFilter,
  ReturnOrderSearchRes,
  ReturnWorkScanHead,
  ReturnWorkScanHeadFilter,
  ReturnWorkScanItemFilter,
  ReturnWorkScanItemRes,
  ReturnWorkScanSaveReq,
  ReturnWorkScanSaveRes,
} from './types'

const PATH = ['return', 'returnWorkScan'] as const

export const returnWorkScanQueryKeys = {
  all: PATH,
  summary: (filter: ReturnWorkScanHeadFilter | null) => [...PATH, 'SUMMARY', filter] as const,
  items: (filter: ReturnWorkScanItemFilter | null) => [...PATH, 'ITEMS', filter] as const,
  orderSearch: (filter: ReturnOrderSearchFilter | null) => [...PATH, 'ORDER_SEARCH', filter] as const,
}

type QueryStateOf<TData> = Omit<UseQueryResult<TData, Error>, 'data'>

interface SummaryQueryData {
  head: ReturnWorkScanHead | null
  nextOrderNumber: number | null
}

/** HEAD 조회. `ordNoMax` 는 결과 첫 행에만 실리므로 여기서 함께 뽑아 둔다. */
export const useGetReturnWorkScanSummary = (
  filter: ReturnWorkScanHeadFilter | null,
): QueryStateOf<SummaryQueryData> & SummaryQueryData => {
  const { data, ...rest } = useQuery<SummaryQueryData, Error>({
    queryKey: returnWorkScanQueryKeys.summary(filter),
    queryFn: async () => {
      if (!filter) return { head: null, nextOrderNumber: null }
      const res = await getReturnWorkScanSummary(filter)
      const first = res.data?.items?.[0]
      if (!first) return { head: null, nextOrderNumber: null }
      return { head: toReturnWorkScanHead(first), nextOrderNumber: first.ordNoMax ?? null }
    },
    enabled: false,
    // 실패 안내는 스캔 화면이 큰 토스트로 한다(scanNotice) — 전역 QueryCache 알림(새 알럿)까지 뜨면 확인 버튼이 다음 스캔을 막는다(#899 리뷰).
    meta: { noErrorMessage: true },
  })

  return { head: data?.head ?? null, nextOrderNumber: data?.nextOrderNumber ?? null, ...rest }
}

/** ITEM 조회 — 매핑 없이 원본 응답을 돌려준다. 반품번호 부여는 훅(`useReturnWorkScan`)이 한다. */
export const useGetReturnWorkScanItems = (
  filter: ReturnWorkScanItemFilter | null,
): QueryStateOf<ReturnWorkScanItemRes[]> & { items: ReturnWorkScanItemRes[] } => {
  const { data, ...rest } = useQuery({
    queryKey: returnWorkScanQueryKeys.items(filter),
    queryFn: async () => {
      if (!filter) return []
      const res = await getReturnWorkScanItems(filter)
      return res.data?.items ?? []
    },
    enabled: false,
    // 실패 안내는 스캔 화면이 큰 토스트로 한다(scanNotice) — 전역 QueryCache 알림(새 알럿)까지 뜨면 확인 버튼이 다음 스캔을 막는다(#899 리뷰).
    meta: { noErrorMessage: true },
  })

  return { items: data ?? [], ...rest }
}

/** 주문검색 팝업 조회. */
export const useGetReturnOrderSearch = (
  filter: ReturnOrderSearchFilter | null,
): QueryStateOf<ReturnOrderSearchRes[]> & { items: ReturnOrderSearchRes[] } => {
  const { data, ...rest } = useQuery({
    queryKey: returnWorkScanQueryKeys.orderSearch(filter),
    queryFn: async () => {
      if (!filter) return []
      const res = await getReturnOrderSearch(filter)
      return res.data?.items ?? []
    },
    enabled: false,
    // 실패 안내는 스캔 화면이 큰 토스트로 한다 — 전역 알림(새 알럿)이 확인 버튼으로 다음 스캔을 막지 않게(#899 리뷰).
    meta: { noErrorMessage: true },
  })

  return { items: data ?? [], ...rest }
}

/** 저장. */
export const useSaveReturnWorkScan = (): UseMutationResult<ReturnWorkScanSaveRes | null, Error, ReturnWorkScanSaveReq> =>
  useMutation({
    mutationFn: async (body: ReturnWorkScanSaveReq) => {
      const res = await saveReturnWorkScan(body)
      return res.data ?? null
    },
  })
